import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useRoute, useNavigation } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import { useGetAiTutorQuery } from "../graphql/generated/graphql";
import { useAppState } from "../contexts/AppStateContext";
import BlankLayout from "../layouts/BlankLayout";
import InteractiveLearningIntroModal from "../containers/companion/InteractiveLearningIntroModal";
import LearningGoalsContainer, { Objective } from "../containers/companion/LearningGoalsContainer";
import { colors } from "../styles/design-system";
import { useAuth } from "../hooks/useAuth";

const LearningActivityView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Interactive Learning Environment">>();
  const { activityId } = route.params || {};
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [introModalVisible, setIntroModalVisible] = useState(false);
  const [objectives, setObjectives] = useState<Objective[]>([]);

  const { handleHideLearningActivityIntro } = useAppState();

  const { meData } = useAuth();
  const { data: aiTutorData, loading: aiTutorLoading } = useGetAiTutorQuery({
    variables: { id: activityId },
    skip: !activityId,
  });

  const activityInfo = aiTutorData?.getAITutor?.data[0];

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // TODO: add previous page routing
      navigation.navigate("Custom School Student Courses");
    }
  };

  const handleCloseIntroModal = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      handleHideLearningActivityIntro();
    }
    setIntroModalVisible(false);
  };

  useEffect(() => {
    if (activityInfo) {
      setObjectives(activityInfo.objectives || []);
    }
  }, [activityInfo]);

  useEffect(() => {
    if (meData?.me?.showLearningActivityIntro) {
      setIntroModalVisible(true);
    }
  }, [meData?.me]);

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomButton
              onPress={handleExitPress}
              size="s"
              text="Back to Chat"
              styleType="secondary"
              accessibilityLabel="Exit button"
            />
            {/* Display the activity name */}
            <CustomText
              text={activityInfo?.title}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              aria-level="1"
              role="heading"
            />
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.content}>{!aiTutorLoading && <LearningGoalsContainer objectives={objectives} />}</View>

        <InteractiveLearningIntroModal visible={introModalVisible} onClose={handleCloseIntroModal} />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[300],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    gap: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  content: {
    flex: 1,
    flexDirection: "row",
    position: "relative",
    backgroundColor: colors.grey[50],
  },
});

export default LearningActivityView;
